import React, {
	useState,
	useEffect,
	useLayoutEffect,
	useRef,
	useCallback,
	useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import moment from 'moment';
import Lightbox from 'react-18-image-lightbox';
import './lightbox.css'; // This only needs to be imported once in the app
import ChatBubble from './ChatBubble';
import MediaStrip, { groupMediaRuns } from './MediaStrip';
import CustomerQuestionnaireDialog from './CustomerQuestionnaireDialog';
import ThreadScheduledMessages from '../scheduled-messages/ThreadScheduledMessages';
import useScheduledMessages, {
	formatScheduledEventText,
	getScheduledEventDate,
} from '../scheduled-messages/useScheduledMessages';
import getWebchatUrl from '../../../../utils/getWebchatUrl';
import { formatTypingNames } from '../../../../utils/formatTypingNames';
import { ArrowDown } from 'lucide-react';
import { ActionSheet } from '@/components/mobile';
import { isTouchViewport } from '@/hooks/use-mobile';
import {
	addConversationToCustomerConversations,
	removeOptimisticMessage,
	setComposerRestore,
} from '../../../../actions/webchat';
import { motion, useReducedMotion } from 'framer-motion';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Orb } from '@/components/ai-employee/setup/parts/orb';
import { TypingDots } from '@/components/ai-employee/setup/parts/typing-dots';

const DEFAULT_MESSAGE_COUNT = 20;
const ESTIMATED_ROW_HEIGHT = 72;
const ROW_OVERSCAN = 8;

const getRowKey = (message) =>
	String(message._id || message.clientId || message.createdAt);

// Where the reader was in a thread when it unmounted. The inbox swaps layouts
// when a phone rotates across the md breakpoint; the next mount of the same
// thread a moment later keeps that place instead of jumping to the newest message.
const FEED_HANDOFF_MAX_AGE_MS = 5000;
let feedHandoff = null;

const takeFeedHandoff = (conversationId) => {
	const handoff = feedHandoff;
	feedHandoff = null;
	if (!handoff || handoff.conversationId !== conversationId) return null;
	if (Date.now() - handoff.at > FEED_HANDOFF_MAX_AGE_MS) return null;
	return handoff;
};

// The "… is typing" bubble at the end of the thread. The AI gets its orb.
const TypingBubble = ({ isAi, children }) => (
	<div className="flex justify-end">
		<p
			role="status"
			className="mr-3 my-0.5 inline-flex items-center gap-2 rounded-[18px_18px_4px] bg-bubble px-3.5 py-2 text-sm text-muted-foreground"
		>
			{isAi && <Orb size="xs" active />}
			<span>{children}</span>
			<TypingDots />
		</p>
	</div>
);

const sameDay = (d1, d2) => {
	if (!d1 || !d2) return false;
	const date1 = new Date(d1);
	const date2 = new Date(d2);
	return (
		date1.getFullYear() === date2.getFullYear() &&
		date1.getMonth() === date2.getMonth() &&
		date1.getDate() === date2.getDate()
	);
};

const isScheduledEvent = (message) =>
	message?.sender === 'event' &&
	typeof message.text === 'string' &&
	message.text.startsWith('Scheduled message for');

// The event Message and its ScheduledMessage are created back-to-back in the
// same request, so their Mongo ObjectIds are adjacent. This lets us reliably
// identify which thread events belong to a live (upcoming) scheduled message
// even after the message text is edited (the event text is never updated).
const OBJECT_ID_REGEX = /^[a-f0-9]{24}$/i;

const objectIdDistance = (a, b) => {
	if (!OBJECT_ID_REGEX.test(a || '') || !OBJECT_ID_REGEX.test(b || '')) {
		return null;
	}
	// Chars 8-18 are the 5-byte per-process random, identical for ids generated
	// by the same server process. We compare only this segment (not the leading
	// 4-byte timestamp, which can differ by ~1s when the two creates straddle a
	// second boundary) and use the 3-byte counter gap as the distance.
	if (a.slice(8, 18) !== b.slice(8, 18)) return null;
	return Math.abs(parseInt(a.slice(18), 16) - parseInt(b.slice(18), 16));
};

// The scheduled message stores the id of its frozen thread event line in
// `_eventMessage` (set at create time for inbox single-schedules). Normalize it
// to a plain string whether the field is a raw id or a populated document.
// Legacy rows and bulk/campaign single-recipient rows leave it unset.
const getLinkedEventMessageId = (scheduledMessage) => {
	const linked = scheduledMessage?._eventMessage;
	if (!linked) return null;
	return String(linked._id || linked);
};

// Returns the set of _eventMessage ids that map to a live upcoming scheduled
// message (rendered in the bottom list) and should therefore be hidden from the
// thread to avoid showing them twice. Sent, deleted, and past-due events are
// left visible as normal markers.
const getLiveScheduledEventMessageIds = (messages, scheduledMessages) => {
	const events = (messages || []).filter(
		(message) => isScheduledEvent(message) && message._id,
	);
	const remaining = [...(scheduledMessages || [])];
	const hidden = new Set();
	const pairedScheduledIds = new Set();

	// Preferred: hide the exact linked line via `_eventMessage`. This is the
	// authoritative pairing for anything created through the inbox single-
	// schedule flow. Rows without the link (legacy data, or bulk/campaign
	// single-recipient rows) fall through to the ObjectId/text heuristics below.
	const eventIdSet = new Set(events.map((event) => String(event._id)));
	(scheduledMessages || []).forEach((scheduledMessage) => {
		const linkedId = getLinkedEventMessageId(scheduledMessage);
		if (
			linkedId &&
			eventIdSet.has(linkedId) &&
			!pairedScheduledIds.has(scheduledMessage._id)
		) {
			hidden.add(linkedId);
			pairedScheduledIds.add(scheduledMessage._id);
		}
	});

	// Fallback: pair by ObjectId proximity (closest pairs first).
	const candidates = [];
	events.forEach((event) => {
		remaining.forEach((scheduledMessage) => {
			const distance = objectIdDistance(event._id, scheduledMessage._id);
			if (distance !== null) {
				candidates.push({ event, scheduledMessage, distance });
			}
		});
	});
	candidates.sort((a, b) => a.distance - b.distance);
	candidates.forEach(({ event, scheduledMessage }) => {
		if (hidden.has(event._id) || pairedScheduledIds.has(scheduledMessage._id)) {
			return;
		}
		hidden.add(event._id);
		pairedScheduledIds.add(scheduledMessage._id);
	});

	// Fallback: exact reconstructed event text.
	events.forEach((event) => {
		if (hidden.has(event._id)) return;
		const match = remaining.find(
			(scheduledMessage) =>
				!pairedScheduledIds.has(scheduledMessage._id) &&
				event.text === formatScheduledEventText(scheduledMessage),
		);
		if (match) {
			hidden.add(event._id);
			pairedScheduledIds.add(match._id);
		}
	});

	return hidden;
};

// Marker text the server writes onto the linked event line when a scheduled
// message is canceled or sent immediately. We mirror it optimistically so the
// thread updates without a manual refresh (the server rewrite is authoritative
// and reconciles to the same text on the next fetch).
const resolvedScheduledEventText = (scheduledMessage, action) =>
	`${
		action === 'delete'
			? 'Canceled scheduled message'
			: 'Sending scheduled message now'
	}: ${scheduledMessage.text}`;

// Find the _eventMessage id (the thread event line paired to a scheduled
// message) so we can rewrite just that marker. Prefers the stored `_eventMessage`
// link and falls back to adjacent ObjectId, then exact reconstructed text, for
// rows created before the link existed.
const findScheduledEventMessageId = (messages, scheduledMessage) => {
	const events = (messages || []).filter(
		(message) => isScheduledEvent(message) && message._id,
	);

	// Preferred: the scheduled message stores its own event line id.
	const linkedId = getLinkedEventMessageId(scheduledMessage);
	if (linkedId && events.some((event) => String(event._id) === linkedId)) {
		return linkedId;
	}

	let best = null;
	events.forEach((event) => {
		const distance = objectIdDistance(event._id, scheduledMessage._id);
		if (distance !== null && (best === null || distance < best.distance)) {
			best = { _eventMessage: event._id, distance };
		}
	});
	if (best) return best._eventMessage;

	const exact = events.find(
		(event) => event.text === formatScheduledEventText(scheduledMessage),
	);
	return exact?._id || null;
};

const ChatFeed = ({
	messages,
	activeConversation,
	loadingNewMessage,
	innerRef,
	scheduledMessagesRefreshKey,
	refreshScheduledMessages,
	registerScheduledMessageResolved,
	// Phones: bubbles open an action sheet on tap or long press. Off by default.
	isMobile = false,
}) => {
	const reduceMotion = useReducedMotion();
	const dispatch = useDispatch();
	const users = useSelector((state) => state.users);
	const messageAutomations = useSelector((state) => state.messageAutomations);
	const auth = useSelector((state) => state.auth);
	const typingIndicator = useSelector((state) => state.typingIndicator);
	// The AI Employee is working on a reply to this customer (ai_status 'thinking').
	const aiEmployeeThinking = useSelector(
		(state) =>
			state.aiEmployee?.statusByConversation?.[activeConversation._id] ===
			'thinking'
	);
	const aiEmployeeDrafting = useSelector(
		(state) =>
			state.aiEmployee?.modeByConversation?.[activeConversation._id] ===
			'assist'
	);

	const webchatApiUrl = useMemo(
		() => getWebchatUrl(window.location.href),
		[]
	);

	const [_questionnaireRequest, setQuestionnaireRequest] = useState(null);
	const [
		showCustomerQuestionnaireDialog,
		setShowCustomerQuestionnaireDialog,
	] = useState(false);

	// _eventMessage id -> optimistic resolved marker text (canceled/sent).
	const [resolvedEventText, setResolvedEventText] = useState({});

	const applyResolvedEventText = useCallback((scheduledMessage, action) => {
		const _eventMessage = findScheduledEventMessageId(messages, scheduledMessage);
		if (!_eventMessage) return;
		setResolvedEventText((prev) => ({
			...prev,
			[_eventMessage]: resolvedScheduledEventText(scheduledMessage, action),
		}));
	}, [messages]);

	useEffect(() => {
		registerScheduledMessageResolved?.(applyResolvedEventText);
		return () => registerScheduledMessageResolved?.(null);
	}, [applyResolvedEventText, registerScheduledMessageResolved]);

	const scheduled = useScheduledMessages({
		customerId: activeConversation?._customer?._id,
		refreshKey: scheduledMessagesRefreshKey,
		enabled: !activeConversation?.isGroup,
		onChanged: refreshScheduledMessages,
		onResolved: applyResolvedEventText,
	});

	// Upcoming event lines newer than the scheduled-message list are hidden too,
	// so they don't show in the thread and then vanish once the list loads and
	// moves them to the scheduled card.
	const { scheduledMessages, listedAt } = scheduled;
	const liveScheduledEventMessageIds = useMemo(() => {
		const hidden = getLiveScheduledEventMessageIds(messages, scheduledMessages);
		const now = Date.now();
		(messages || []).forEach((message) => {
			if (!isScheduledEvent(message) || !message._id || hidden.has(message._id)) return;
			const isNewerThanList =
				listedAt === null || new Date(message.createdAt).getTime() > listedAt;
			const sendDate = getScheduledEventDate(message.text);
			if (isNewerThanList && sendDate && sendDate.valueOf() > now) {
				hidden.add(message._id);
			}
		});
		return hidden;
	}, [messages, scheduledMessages, listedAt]);
	const [messageCount, setMessageCount] = useState(DEFAULT_MESSAGE_COUNT);
	const latestMessageCountRef = useRef(messageCount);
	latestMessageCountRef.current = messageCount;
	const [lightbox, setLightbox] = useState(null);

	const timeoutId = useRef(null);
	const messagesEndRef = useRef(null);
	const contentWrapperRef = useRef(null);
	const listRef = useRef(null);
	const [listOffset, setListOffset] = useState(0);
	const containerRef = innerRef || useRef();
	const wasNearBottomRef = useRef(true);
	// Set while a page of older messages is being revealed, so scroll and wheel
	// events at the top don't reveal several pages before the view is re-anchored.
	const isPagingRef = useRef(false);
	const isMobileRef = useRef(isMobile);
	isMobileRef.current = isMobile;
	const conversationIdRef = useRef(activeConversation._id);
	conversationIdRef.current = activeConversation._id;

	// One action sheet for the whole thread; the bubble that was pressed fills it.
	const [bubbleActions, setBubbleActions] = useState(null);
	const [bubbleActionsOpen, setBubbleActionsOpen] = useState(false);
	const showBubbleActions = useCallback((config) => {
		setBubbleActions(config);
		setBubbleActionsOpen(true);
	}, []);
	// Phones: a "jump to latest" button once the reader scrolls up.
	const [showJumpToLatest, setShowJumpToLatest] = useState(false);

	const isNearBottom = useCallback(() => {
		const container = containerRef.current;
		if (!container) return true;
		return (
			container.scrollHeight - container.scrollTop - container.clientHeight < 150
		);
	}, [containerRef]);

	const scrollToBottom = useCallback(() => {
		// Phones: scroll only the thread. scrollIntoView also scrolls the page
		// behind it, which pushes the header off screen when the keyboard is up.
		if (isMobileRef.current && containerRef.current) {
			containerRef.current.scrollTop = containerRef.current.scrollHeight;
			return;
		}
		if (messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({
				behavior: 'instant',
				block: 'end',
			});
		}
	}, [containerRef]);

	useLayoutEffect(() => {
		// Position the newly selected thread before its short fade begins.
		const handoff = takeFeedHandoff(activeConversation._id);
		wasNearBottomRef.current = !handoff;
		isPagingRef.current = false;
		setMessageCount(handoff?.messageCount || DEFAULT_MESSAGE_COUNT);
		setShowJumpToLatest(Boolean(handoff && isMobileRef.current));
		setBubbleActionsOpen(false);
		scrollToBottom();
		if (handoff) {
			requestAnimationFrame(() => {
				const container = containerRef.current;
				if (!container) return;
				container.scrollTop = Math.max(
					0,
					container.scrollHeight - container.clientHeight - handoff.fromBottom,
				);
			});
		}
		// Optimistic markers are per-conversation; clear them on switch.
		setResolvedEventText({});
	}, [activeConversation._id, scrollToBottom]);

	// The virtualizer (anchorTo: 'end') keeps the row the reader is on in place
	// as older rows are added above it and measured.
	const revealOlderMessages = useCallback(() => {
		if (isPagingRef.current || messageCount >= messages.length) return;
		isPagingRef.current = true;
		setMessageCount((prevCount) => prevCount + DEFAULT_MESSAGE_COUNT);
		requestAnimationFrame(() => {
			isPagingRef.current = false;
		});
	}, [messageCount, messages.length]);

	const handleScroll = useCallback(() => {
		const container = containerRef.current;
		if (!container) return;

		wasNearBottomRef.current = isNearBottom();
		if (isMobileRef.current) setShowJumpToLatest(!wasNearBottomRef.current);

		if (container.scrollTop < 50) revealOlderMessages();
	}, [containerRef, revealOlderMessages, isNearBottom]);

	const debounce = useCallback((callback, delay) => {
		clearTimeout(timeoutId.current);
		timeoutId.current = setTimeout(callback, delay);
	}, []);

	const handleWheel = useCallback(
		(event) => {
			const container = containerRef.current;

			if (event.deltaY < 0 && container && container.scrollTop < 50) {
				revealOlderMessages();
			}
		},
		[containerRef, revealOlderMessages]
	);

	const debouncedHandleWheel = useCallback(
		(event) => {
			debounce(() => {
				handleWheel(event);
			}, 50);
		},
		[debounce, handleWheel]
	);

	useEffect(() => {
		const container = containerRef.current;
		if (container) {
			container.addEventListener('wheel', debouncedHandleWheel);
			container.addEventListener('scroll', handleScroll);
		}

		return () => {
			if (container) {
				container.removeEventListener('wheel', debouncedHandleWheel);
				container.removeEventListener('scroll', handleScroll);
			}
			clearTimeout(timeoutId.current);
		};
	}, [containerRef, debouncedHandleWheel, handleScroll]);

	useEffect(() => {
		if (wasNearBottomRef.current) {
			scrollToBottom();
		}
	}, [messages.length, scrollToBottom]);

	// Images and the pinned scheduled messages load after the initial scroll and
	// grow the thread; keep it pinned to the bottom if the user was already there.
	useEffect(() => {
		const content = contentWrapperRef.current;
		if (!content || typeof ResizeObserver === 'undefined') return;

		const observer = new ResizeObserver(() => {
			if (wasNearBottomRef.current) scrollToBottom();
		});
		observer.observe(content);
		return () => observer.disconnect();
	}, [activeConversation._id, scrollToBottom]);

	// Touch screens: the thread shrinks when the keyboard opens or the phone
	// rotates. Keep the newest message in view if the reader was at the bottom.
	useEffect(() => {
		const container = containerRef.current;
		if (!container || typeof ResizeObserver === 'undefined' || !isTouchViewport()) return;
		const observer = new ResizeObserver(() => {
			if (wasNearBottomRef.current) {
				container.scrollTop = container.scrollHeight;
			}
		});
		observer.observe(container);
		return () => observer.disconnect();
	}, [containerRef]);

	// Remember the reader's place for a remount of the same thread (feedHandoff).
	useLayoutEffect(
		() => () => {
			const container = containerRef.current;
			if (!container || !conversationIdRef.current) return;
			const fromBottom =
				container.scrollHeight - container.scrollTop - container.clientHeight;
			feedHandoff =
				fromBottom > 150
					? {
							conversationId: conversationIdRef.current,
							fromBottom,
							messageCount: latestMessageCountRef.current,
							at: Date.now(),
						}
					: null;
		},
		[containerRef],
	);

	useEffect(() => {
		if (loadingNewMessage) {
			scrollToBottom();
		}
	}, [loadingNewMessage, scrollToBottom]);

	useEffect(() => {
		const matchingTyper = typingIndicator.filter(
			(i) => i._customerConversation === activeConversation._id
		);
		if (matchingTyper.length || aiEmployeeThinking) {
			scrollToBottom();
		}
	}, [typingIndicator, aiEmployeeThinking, activeConversation._id, scrollToBottom]);

	const closeCustomerQuestionnaireDialog = useCallback(() => {
		setShowCustomerQuestionnaireDialog(false);
		setQuestionnaireRequest(null);
	}, []);

	const fetchCustomerFeedback = useCallback(({ _questionnaireRequest }) => {
		setShowCustomerQuestionnaireDialog(true);
		setQuestionnaireRequest(_questionnaireRequest);
	}, []);

	const getMessageSentBy = useCallback(
		(message) => {
			if (message.sender === 'ai') {
				return 'AI Employee';
			}

			// group conversations: attribute inbound messages to the participant
			if (message.sender === 'customer') {
				if (activeConversation?.isGroup) {
					const senderId =
						message._customer?._id?.toString?.() ||
						message._customer?.toString?.() ||
						message._customer;
					const participant = (activeConversation._customers || []).find(
						(c) => (c?._id?.toString?.() || c?._id || c) === senderId
					);
					return participant?.name || participant?.phone || '';
				}
				return '';
			}

			if (!users) {
				return '';
			}

			if (message._messageAutomation && messageAutomations) {
				const messageAutomationArr = messageAutomations.filter(
					(ma) => ma._id === message._messageAutomation
				);
				if (messageAutomationArr.length) {
					return (
						messageAutomationArr[0].title || 'Message Automation'
					);
				}
				return 'Message Automation';
			}

			const user = users.find((u) => u._id === message._user);
			return user ? user.name || user.username || '' : '';
		},
		[users, messageAutomations, activeConversation]
	);

	const handleConfirmRemoveMessageClick = useCallback(
		async ({ _message }) => {
			try {
				const res = await axios.post(
					`${webchatApiUrl}/api/webchat/set-message-inactive`,
					{
						_message,
						_customer: activeConversation._customer?._id,
					}
				);

				if (res.data.error) {
					return alert(
						'Sorry, there was an error. Could not remove message.'
					);
				}

				const { customerConversation } = res.data;
				dispatch(
					addConversationToCustomerConversations(
						customerConversation,
						true
					)
				);
			} catch (err) {
				console.error('Error removing message:', err);
				alert('Sorry, there was an error. Could not remove message.');
			}
		},
		[activeConversation, webchatApiUrl, dispatch]
	);

	const handleEditNote = useCallback(
		async ({ _message, text, mentions, requestId, expectedRevision }) => {
			try {
				const res = await axios.post(`${webchatApiUrl}/api/webchat/edit-note`, {
					_message, mentions, requestId, expectedRevision,
					_customer: activeConversation._customer?._id,
					_business: activeConversation._business,
					text,
				});

				if (!res.data || res.data.error) {
					return {
						error: true,
						message: res.data?.message || 'Could not save your note.',
					};
				}

				dispatch(addConversationToCustomerConversations(res.data.customerConversation, true));
				return { error: false };
			} catch (err) {
				console.error('note_edit_failed', { name: err.name, status: err.response?.status });
				return { error: true, latestNote: err.response?.data?.latestNote, message: err.response?.data?.message || 'Could not save your note.' };
			}
		},
		[activeConversation, webchatApiUrl, dispatch],
	);

	const showImageLightbox = useCallback((mediaUrl, images = [mediaUrl]) => {
		setLightbox({ images, index: Math.max(0, images.indexOf(mediaUrl)) });
	}, []);

	const renderImageLightbox = () => {
		if (!lightbox) return null;
		const { images, index } = lightbox;
		const hasGallery = images.length > 1;
		const step = (delta) =>
			setLightbox({ images, index: (index + delta + images.length) % images.length });
		return (
			<Lightbox
				mainSrc={images[index]}
				nextSrc={hasGallery ? images[(index + 1) % images.length] : undefined}
				prevSrc={hasGallery ? images[(index - 1 + images.length) % images.length] : undefined}
				onMoveNextRequest={() => step(1)}
				onMovePrevRequest={() => step(-1)}
				onCloseRequest={() => setLightbox(null)}
				reactModalStyle={{ zIndex: 99999 }}
			/>
		);
	};

	const renderTypingBubble = () => {
		const matchingTyper = typingIndicator.filter(
			(i) => i._customerConversation === activeConversation._id
		);

		// Shown above anyone typing, so a teammate can see the AI is about to reply.
		const aiEmployeeBubble = aiEmployeeThinking ? (
			<TypingBubble isAi>
				{aiEmployeeDrafting
					? 'AI Employee is drafting a reply'
					: 'AI Employee is thinking'}
			</TypingBubble>
		) : null;

		if (!matchingTyper.length) {
			return aiEmployeeBubble;
		}

		// Check if AI Assistant is typing
		const aiTyping = matchingTyper.find(
			(t) => t.whoIsTyping === 'AI Assistant'
		);

		return (
			<>
				{aiEmployeeBubble}
				{aiTyping ? (
					// Show AI status based on the aiStatus property
					<TypingBubble isAi>
						{aiTyping.aiStatus === 'thinking'
							? 'AI is thinking'
							: 'AI is typing'}
					</TypingBubble>
				) : (
					// Regular user typing
					<TypingBubble>
						{formatTypingNames(matchingTyper.map((t) => t.whoIsTyping))}
					</TypingBubble>
				)}
			</>
		);
	};

	const formatDateLabel = useCallback((date) => {
		const messageDate = moment(date);
		const today = moment().startOf('day');
		const yesterday = moment().subtract(1, 'days').startOf('day');

		if (messageDate.isSame(today, 'day')) {
			return 'Today';
		} else if (messageDate.isSame(yesterday, 'day')) {
			return 'Yesterday';
		} else {
			// Phones: the short weekday and month fit on one line.
			return messageDate.format(isMobile ? 'llll' : 'LLLL');
		}
	}, [isMobile]);

	const feedRows = useMemo(() => {
		const visibleMessages = messages
			.slice()
			.filter(
				(message) =>
					activeConversation.isGroup ||
					!isScheduledEvent(message) ||
					!liveScheduledEventMessageIds.has(message._id),
			)
			.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
			.slice(-messageCount)
			.map((message) =>
				resolvedEventText[message._id]
					? { ...message, text: resolvedEventText[message._id] }
					: message,
			);

		// Only embedded form submissions create a `channel: 'form'` bubble (via
		// inboundFormSubmission), immediately followed by the "View responses"
		// event. Merge those two into a single bubble: attach the questionnaire
		// request id to the form message and hide the standalone event. The
		// SMS/messenger questionnaire flow posts the event without a form bubble,
		// so it stays standalone.
		const isFormSubmission = (m) =>
			m && m.sender === 'customer' && m.channel === 'form';
		const isQuestionnaireEvent = (m) =>
			m &&
			m.sender === 'event' &&
			typeof m.text === 'string' &&
			m.text.includes('@customer-response');

		const mergedQuestionnaireRequestByMessage = {};
		const mergedEventIdsToHide = new Set();

		for (let idx = 0; idx < visibleMessages.length; idx += 1) {
			const current = visibleMessages[idx];
			const next = visibleMessages[idx + 1];

			if (isFormSubmission(current) && isQuestionnaireEvent(next)) {
				const _questionnaireRequest =
					next.text.split('_questionnaireRequest=')[1];

				if (_questionnaireRequest) {
					mergedQuestionnaireRequestByMessage[current._id] =
						_questionnaireRequest;
					if (next._id) {
						mergedEventIdsToHide.add(next._id);
					}
				}
			}
		}

		// Hidden because it was merged into the preceding form bubble.
		const shownMessages = visibleMessages.filter(
			(message) => !(message._id && mergedEventIdsToHide.has(message._id)),
		);
		let previous = null;
		return groupMediaRuns(shownMessages).map(({ message, mediaGroup }) => {
			const row = {
				message,
				mediaGroup,
				showDateLabel: !previous || !sameDay(previous.createdAt, message.createdAt),
				questionnaireRequestId:
					mergedQuestionnaireRequestByMessage[message._id],
			};
			previous = mediaGroup ? mediaGroup[mediaGroup.length - 1] : message;
			return row;
		});
	}, [
		messages,
		activeConversation.isGroup,
		liveScheduledEventMessageIds,
		messageCount,
		resolvedEventText,
	]);

	const getItemKey = useCallback(
		(index) => getRowKey(feedRows[index].message),
		[feedRows],
	);

	const virtualizer = useVirtualizer({
		count: feedRows.length,
		getScrollElement: () => containerRef.current,
		estimateSize: () => ESTIMATED_ROW_HEIGHT,
		overscan: ROW_OVERSCAN,
		getItemKey,
		scrollMargin: listOffset,
		anchorTo: 'end',
	});

	// The virtualizer measures rows from the top of the scroll container, so it
	// needs to know how far down the list starts.
	useLayoutEffect(() => {
		if (listRef.current) setListOffset(listRef.current.offsetTop);
	}, [activeConversation._id]);

	const renderChatBubbles = () => {
		return (
			<div>
				<div
					ref={listRef}
					style={{ height: virtualizer.getTotalSize(), position: 'relative' }}
				>
				{virtualizer.getVirtualItems().map((virtualRow) => {
					const { message, mediaGroup, showDateLabel, questionnaireRequestId } =
						feedRows[virtualRow.index];

					let dateDiv = null;
					if (showDateLabel) {
						dateDiv = (
							<div className="flex flex-row justify-center mx-auto my-4">
								<div className="px-3 py-1 border border-border/70 bg-muted/50 rounded-full text-xs text-muted-foreground font-normal">
									{formatDateLabel(message.createdAt)}
								</div>
							</div>
						);
					}

					// Only animate recent/pending/failed bubbles so historical
					// messages don't cascade on mount or when paginating.
					const createdAtMs = message.createdAt
						? new Date(message.createdAt).getTime()
						: 0;
					const isRecent =
						message.status === 'sending' ||
						message.status === 'failed' ||
						(createdAtMs && Date.now() - createdAtMs < 2000);

					const handleRestoreBubble =
						message.status === 'failed' && message.clientId
							? () => {
									const attachmentUrls = Array.isArray(
										message.attachmentUrls
									)
										? message.attachmentUrls
										: message.media
											? [message.media]
											: [];
									dispatch(
										setComposerRestore({
											text: message.composerText ?? message.text ?? '',
											attachmentUrls,
											clientId: message.clientId,
											key: `${message.clientId}-${Date.now()}`,
											// A failed note goes back into Note mode (never the
											// customer reply) with its tags and files; the same
											// request id lets a note that did save not save twice.
											...(message.sender === 'note'
												? {
														sender: 'note',
														mentions: message.mentions,
														attachments: message.attachments,
														requestId: message.clientId,
													}
												: {}),
										})
									);
									dispatch(
										removeOptimisticMessage({
											_conversation: activeConversation._id,
											clientId: message.clientId,
										})
									);
								}
							: undefined;

					// Positioned with `top` rather than a transform so open bubble
					// menus aren't trapped under the next row's stacking context.
					return (
						<div
							key={virtualRow.key}
							data-index={virtualRow.index}
							ref={virtualizer.measureElement}
							style={{
								position: 'absolute',
								top: virtualRow.start - virtualizer.options.scrollMargin,
								left: 0,
								width: '100%',
							}}
						>
						<motion.div
							initial={isRecent && !reduceMotion ? { opacity: 0, y: 6 } : false}
							animate={{ opacity: 1, y: 0 }}
							transition={reduceMotion ? { duration: 0 } : {
								type: 'spring',
								stiffness: 260,
								damping: 24,
								mass: 0.6,
							}}
						>
							{dateDiv}
							{mediaGroup ? (
								<MediaStrip
									messages={mediaGroup}
									sentBy={getMessageSentBy(message)}
									showImageLightbox={showImageLightbox}
								/>
							) : (
							<ChatBubble
								_message={message._id}
								_call={message._call}
								createdAt={message.createdAt}
								sender={message.sender}
								text={message.text}
                mentions={message.mentions} noteRevision={message.noteRevision}
								attachments={message.attachments}
                _customer={activeConversation._customer?._id} _business={activeConversation._business}
								media={message.media}
								aiMeta={message.aiMeta}
								sentBy={getMessageSentBy(message)}
								smsStatus={message.SmsStatus}
								errorCode={message.SmsErrorCode}
								channel={message.channel}
								questionnaireRequestId={questionnaireRequestId}
								accessLevel={auth.user.accessLevel}
								_user={message._user}
								currentUserId={auth.user._id}
								updatedAt={message.updatedAt}
								onEditNote={handleEditNote}
								handleConfirmRemoveMessageClick={
									handleConfirmRemoveMessageClick
								}
								fetchCustomerFeedback={fetchCustomerFeedback}
								showImageLightbox={showImageLightbox}
								linkedWorkflow={message.linkedWorkflow}
								status={message.status}
								onRestore={handleRestoreBubble}
								isMobile={isMobile}
								onShowActions={isMobile ? showBubbleActions : undefined}
							/>
							)}
						</motion.div>
						</div>
					);
				})}
				</div>
			</div>
		);
	};

	const renderCustomerFeedbackDialog = () => {
		if (!_questionnaireRequest) {
			return null;
		}
		return (
			<CustomerQuestionnaireDialog
				_questionnaireRequest={_questionnaireRequest}
				open={showCustomerQuestionnaireDialog}
				closeDialog={closeCustomerQuestionnaireDialog}
			/>
		);
	};

	return (
		<div className="relative flex min-h-0 flex-col flex-grow overflow-hidden">
			{/* Gradient fade at top to indicate more messages */}
			<div className="absolute top-0 left-0 right-0 h-3 bg-gradient-to-b from-background to-transparent pointer-events-none z-10" />

			<div
				ref={containerRef}
				className="flex flex-col flex-grow overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400/50 scrollbar-track-transparent max-md:scroll-touch"
				style={{
					scrollbarWidth: 'thin',
					scrollbarColor: 'rgba(155, 155, 155, 0.5) transparent',
					msOverflowStyle: 'auto',
				}}
				id="chatfeed"
			>
				<div
					key={activeConversation._id}
					ref={contentWrapperRef}
					className="conversation-enter relative py-1 sm:px-1"
					style={{ overflowAnchor: 'none' }}
				>
					{renderImageLightbox()}
					{renderChatBubbles()}
					{!activeConversation.isGroup && (
						<ThreadScheduledMessages
							scheduledMessages={scheduled.scheduledMessages}
							activeActionId={scheduled.activeActionId}
							sendNow={scheduled.sendNow}
							deleteScheduledMessage={scheduled.deleteScheduledMessage}
							dialogs={scheduled.dialogs}
						/>
					)}
					{renderTypingBubble()}
				</div>
				<div
					ref={messagesEndRef}
					style={{ overflowAnchor: 'auto', height: 1 }}
				/>
				{renderCustomerFeedbackDialog()}
			</div>
			{isMobile && showJumpToLatest && (
				<button
					type="button"
					aria-label="Jump to latest message"
					onClick={() => {
						wasNearBottomRef.current = true;
						setShowJumpToLatest(false);
						const container = containerRef.current;
						if (container) {
							container.scrollTo({
								top: container.scrollHeight,
								behavior: reduceMotion ? 'auto' : 'smooth',
							});
						}
					}}
					className="absolute bottom-3 right-3 z-10 flex size-11 items-center justify-center rounded-full border bg-background/95 text-foreground shadow-md backdrop-blur active:bg-muted"
				>
					<ArrowDown className="size-5" aria-hidden />
				</button>
			)}
			{isMobile && bubbleActions && (
				<ActionSheet
					open={bubbleActionsOpen}
					onOpenChange={setBubbleActionsOpen}
					title={bubbleActions.title}
					hideTitle
					header={bubbleActions.header}
					sections={bubbleActions.sections}
				/>
			)}
		</div>
	);
};

export default ChatFeed;
